import { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Play, Clock, CheckCircle, User, LogOut, ChevronRight, Loader2, PlayCircle } from 'lucide-react';
import R2Player from './video/R2Player';
import { useVideoProgress } from '@/hooks/useVideoProgress';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';

interface VideoCourseProps {
  onLogout: () => void;
}

interface Video {
  id: string;
  title: string;
  duration: string;
  completed: boolean;
  thumbnail: string;
  description: string;
}

interface VideoData extends Video {
  guid: string;
  resourceLinks: string[];
}

const mockVideos: VideoData[] = [
  {
    id: '1',
    title: 'Ինտրո դեպի «Վայբ Քոդինգ»',
    duration: '23:47',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=400&h=225&fit=crop',
    description: 'Բարի գալուստ Վայբ Քոդինգ դասընթաց: Ծանոթացի Վայբ Քոդինգի հիմունքների հետ:',
    guid: 'f00006de-0ae7-46e2-9870-cfd6e762eb72',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/6uwnrT2IjNl']
  },
  {
    id: '2',
    title: 'Վայբ Քոդինգի Մեծ Բառարան',
    duration: '33:34',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=225&fit=crop',
    description: 'Սովորի Վայբ Քոդինգի հետ առնչվող հիմնական բառարանն ու տերմինները:',
    guid: 'e509d080-40bc-4977-8fdc-008356dce958',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/5MuBFH4R9r8']
  },
  {
    id: '3',
    title: 'Ֆունդամենտ',
    duration: '15:57',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=225&fit=crop',
    description: 'Խորացի Վայբ Քոդինգի ֆունդամենտալ սկզբունքների մեջ:',
    guid: 'd92c3e3f-e777-4f76-b4a6-fd6f20786a18',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/4vpmlmk9ljz']
  },
  {
    id: '4',
    title: 'Արագ արդյունք',
    duration: '36:22',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=400&h=225&fit=crop',
    description: 'Սովորի արագ արդյունքներ ստանալ Վայբ Քոդինգով:',
    guid: '8ad83a10-0e8b-4aad-98bf-4177220b84c6',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/Zgdo9JYjTK']
  },
  {
    id: '5',
    title: 'Նկարներ',
    duration: '17:16',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?w=400&h=225&fit=crop',
    description: 'Ուսումնասիրի նկարների հետ աշխատանքի բոլոր նրբությունները:',
    guid: '508eb111-ece6-4008-8fcd-0f1d4012ee67',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/6PBvJI1edxV']
  },
  {
    id: '6',
    title: 'Վիդոսներ',
    duration: '15:23',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=400&h=225&fit=crop',
    description: 'Վիդոսների ինտեգրումը ու օպտիմիզացիան քո պրոյեկտներում:',
    guid: '94445d3b-dbda-48fc-9c84-348857fcd90c',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/4ubyqP7WYS1']
  },
  {
    id: '7',
    title: 'Ֆոնտեր',
    duration: '13:49',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=225&fit=crop',
    description: 'Ֆոնտերի ճիշտ ընտրությունը ու կիրառումը դիզայնում:',
    guid: '59b95fd3-b416-4083-b071-63bfb183bf8f',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/30kzlRJtYz8']
  },
  {
    id: '8',
    title: 'Ինտրո դեպի NPM',
    duration: '09:56',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=225&fit=crop',
    description: 'NPM-ի հիմունքները ու փաթեթների մենեջմենտը:',
    guid: 'ea841700-1a8f-4780-8dbe-a8b105e2fcf6',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/7tStVfrzLmi']
  },
  {
    id: '9',
    title: 'SEO-ական հարցեր',
    duration: '27:11',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=400&h=225&fit=crop',
    description: 'SEO օպտիմիզացիայի հիմունքները և լավագույն պրակտիկաները:',
    guid: 'b85d6f36-cb57-40b7-8aad-030315d894ec',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/Z9GfQUlCJc']
  },
  {
    id: '10',
    title: 'ԲՈՆՈՒՍ 1 - Nostalgia AI Լենդինգ',
    duration: '54:49',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?w=400&h=225&fit=crop',
    description: 'Գործնական օրինակ՝ Nostalgia AI լենդինգ էջի ստեղծումը:',
    guid: 'fafd6392-4e7a-4fda-9b8a-4254c67798f3',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/kIUNEv6x8X']
  },
  {
    id: '11',
    title: 'ԲՈՆՈՒՍ 2 - Nostalgia AI App',
    duration: '1:07:47',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=400&h=225&fit=crop',
    description: 'Nostalgia AI app-ի ստեղծումը:',
    guid: '5cb5dcdb-7a54-49e6-873d-0ca198166883',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/kIUNEv6x8X']
  },
  {
    id: '12',
    title: 'ԲՈՆՈՒՍ 3 - Ոնց Ծախել Քո Սերվիսը',
    duration: '16:36',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?w=400&h=225&fit=crop',
    description: 'Սովորի ոնց արդյունավետ կերպով ծախել քո սերվիսները:',
    guid: '3dc86984-d835-47fc-9876-1afb757fa0cd',
    resourceLinks: ['https://link.excalidraw.com/l/7AuiuVJjtqw/8VhwFVZjVi0']
  },
  {
    id: '13',
    title: 'ԲՈՆՈՒՍ 4 - Անվտանգություն',
    duration: '32:27',
    completed: false,
    thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=225&fit=crop',
    description: 'Սովորի ոնց պաշտպանես սարքածդ իմանալով հիմնական անվտանգային հարցերը։',
    guid: '7ec57cec-9232-42b0-bda4-23de833be7c4',
    resourceLinks: ['https://gemini.google.com/share/6438c8ba2389']
  }
];

export default function VideoCourse({ onLogout }: VideoCourseProps) {
  const [selectedVideo, setSelectedVideo] = useState<VideoData>(mockVideos[0]);
  const [markingComplete, setMarkingComplete] = useState<string | null>(null);
  const [videoPositions, setVideoPositions] = useState<Record<string, number>>({});
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [webhookSent, setWebhookSent] = useState(false);
  const [checkingCompletion, setCheckingCompletion] = useState(true);
  const [hasShownPopupBefore, setHasShownPopupBefore] = useState(false);
  const { toast } = useToast();
  
  // Load saved video positions from localStorage
  useEffect(() => {
    const positions: Record<string, number> = {};
    mockVideos.forEach(video => {
      const saved = localStorage.getItem(`video_position_${video.id}`);
      if (saved) {
        positions[video.id] = parseFloat(saved);
      }
    });
    setVideoPositions(positions);
  }, []);

  // Format time helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle video progress updates
  const handleVideoProgress = useCallback((videoId: string, currentTime: number, duration: number) => {
    setVideoPositions(prev => ({
      ...prev,
      [videoId]: currentTime
    }));
  }, []);

  // Resume video from saved position
  const handleResumeVideo = (videoId: string) => {
    const video = mockVideos.find(v => v.id === videoId);
    if (video) {
      setSelectedVideo(video);
      // R2Player automatically resumes from the saved position
    }
  };
  
  // Get user info from localStorage with better error handling
  const [userInfo, setUserInfo] = useState<{userInfo: any, userId: string}>(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('vibeAuth') : null;
      if (!raw) return { userInfo: null, userId: '' };
      const auth = JSON.parse(raw);
      const userId = auth.userInfo?.id || '';
      
      // Log for debugging
      if (!userId) {
        console.warn('⚠️ No userId found in localStorage auth data:', auth);
      } else {
        console.log('✅ User authenticated with ID:', userId);
      }
      
      return { 
        userInfo: auth.userInfo,
        userId
      };
    } catch (error) {
      console.error('❌ Error parsing localStorage auth:', error);
      return { userInfo: null, userId: '' };
    }
  });

  // Re-check authentication periodically
  useEffect(() => {
    const checkAuth = () => {
      try {
        const raw = localStorage.getItem('vibeAuth');
        if (raw) {
          const auth = JSON.parse(raw);
          const userId = auth.userInfo?.id || '';
          if (userId !== userInfo.userId) {
            console.log('🔄 Auth state changed, updating user info');
            setUserInfo({
              userInfo: auth.userInfo,
              userId
            });
          }
        }
      } catch (error) {
        console.error('❌ Error checking auth state:', error);
      }
    };

    // Check auth every 30 seconds
    const interval = setInterval(checkAuth, 30000);
    return () => clearInterval(interval);
  }, [userInfo.userId]);

  // Check database for existing course completion on mount
  useEffect(() => {
    const checkDatabaseCompletion = async () => {
      if (!userInfo.userInfo?.email) {
        setCheckingCompletion(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('course_completions')
          .select('*')
          .eq('user_email', userInfo.userInfo.email)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('❌ Error checking completion status:', error);
        } else if (data) {
          console.log('✅ Found existing completion record:', data);
          setWebhookSent(true);
          setHasShownPopupBefore(true); // Mark that popup was already shown before
          // Migrate localStorage if it doesn't exist
          const webhookKey = `webhook_sent_${userInfo.userInfo.email}`;
          localStorage.setItem(webhookKey, 'true');
        } else {
          console.log('🔍 No completion record found, webhook not sent yet');
        }
      } catch (error) {
        console.error('💥 Exception checking completion:', error);
      } finally {
        setCheckingCompletion(false);
      }
    };

    if (userInfo.userInfo?.email) {
      checkDatabaseCompletion();
    } else {
      setCheckingCompletion(false);
    }
  }, [userInfo.userInfo?.email]);

  // Use video progress hook with fallback
  const { videoProgress, updateVideoProgress, resetAllProgress, isLoading } = useVideoProgress({
    userId: userInfo.userId || 'anonymous',
    initialVideos: mockVideos.map(v => ({ id: v.id, completed: v.completed }))
  });

  // Create videos with progress from hook
  const videos = useMemo(() => {
    return mockVideos.map(video => ({
      ...video,
      completed: videoProgress[video.id] || false
    }));
  }, [videoProgress]);

  // Update selected video with progress
  const currentSelectedVideo = useMemo(() => {
    return {
      ...selectedVideo,
      completed: videoProgress[selectedVideo.id] || false
    };
  }, [selectedVideo, videoProgress]);

  const [userLabel] = useState<string>(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('vibeAuth') : null;
      if (!raw) return '';
      const { userInfo } = JSON.parse(raw);
      const email = userInfo?.email ?? '';
      const name = userInfo?.display_name ?? '';
      return [email, name].filter(Boolean).join(' • ');
    } catch {
      return '';
    }
  });

  const completedCount = videos.filter(v => v.completed).length;
  const progressPercentage = (completedCount / videos.length) * 100;

  // Check if all videos are completed and send webhook
  useEffect(() => {
    // Don't proceed if still checking database completion status
    if (checkingCompletion) return;
    
    const allCompleted = videos.length > 0 && videos.every(v => v.completed);
    
    // Only show congratulations and send webhook if not already done
    if (allCompleted && !webhookSent && !hasShownPopupBefore && userInfo.userInfo) {
      console.log('🎉 All videos completed! Sending webhook...');
      
      const userEmail = userInfo.userInfo.email;
      
      setWebhookSent(true); // Prevent multiple calls immediately
      setHasShownPopupBefore(true); // Mark that popup has been shown
      setShowCongratulations(true); // Show popup when first completing
      
      // Send webhook with better error handling
      supabase.functions.invoke('course-completion-webhook', {
        body: {
          userName: userInfo.userInfo.display_name || userInfo.userInfo.email || 'Unknown User',
          userEmail: userInfo.userInfo.email || 'no-email@example.com'
        }
      }).then(({ data, error }) => {
        if (error) {
          console.error('❌ Webhook error:', error);
          // Reset webhook sent status on error so it can be retried
          setWebhookSent(false);
          // Don't show error to user unless it's critical
          if (error.message && !error.message.includes('404')) {
            toast({
              title: "Ծանուցում",
              description: "Դասընթացի ավարտի մասին ծանուցումը չգրանցվեց, բայց Ձեր առաջընթացը պահպանված է:",
            });
          }
        } else {
          console.log('✅ Webhook sent successfully:', data);
          // Save to localStorage as backup
          if (userEmail) {
            const webhookKey = `webhook_sent_${userEmail}`;
            localStorage.setItem(webhookKey, 'true');
          }
        }
      }).catch(err => {
        console.error('💥 Webhook exception:', err);
        // Reset webhook sent status on error so it can be retried
        setWebhookSent(false);
        // Only show user-facing error if it's not a network issue
        if (err && !err.message?.includes('NetworkError')) {
          toast({
            title: "Ծանուցում",
            description: "Դասընթացի ավարտի մասին ծանուցումը չգրանցվեց, բայց Ձեր առաջընթացը պահպանված է:",
          });
        }
      });
    }
  }, [videos, webhookSent, hasShownPopupBefore, userInfo.userInfo, toast, checkingCompletion]);
  const handleVideoComplete = async (videoId: string) => {
    console.log('🎬 handleVideoComplete called for video:', videoId);
    
    setMarkingComplete(videoId);
    
    try {
      const result = await updateVideoProgress(videoId, true);
      
      if (result?.success) {
        toast({
          title: "Հաջող!",
          description: "Վիդեոն նշվեց որպես ավարտված:",
        });
        
        // Update selected video if it's the same as the completed one
        if (selectedVideo.id === videoId) {
          setSelectedVideo(prev => ({ ...prev, completed: true }));
        }
      } else {
        console.error('❌ Failed to mark video as complete:', result?.error);
        toast({
          title: "Սխալ",
          description: `Չհաջողվեց նշել վիդեոն որպես ավարտված: ${result?.error || 'Անհայտ սխալ'}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('💥 Exception in handleVideoComplete:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց նշել վիդեոն որպես ավարտված: Անհայտ սխալ",
        variant: "destructive",
      });
    } finally {
      setMarkingComplete(null);
    }
  };

  const handleVideoUnmark = async (videoId: string) => {
    console.log('🎬 handleVideoUnmark called for video:', videoId);
    
    setMarkingComplete(videoId);
    
    try {
      const result = await updateVideoProgress(videoId, false);
      
      if (result?.success) {
        toast({
          title: "Հաջող!",
          description: "Վիդեոն նշվեց որպես չդիտած:",
        });
        
        // Update selected video if it's the same as the unmarked one
        if (selectedVideo.id === videoId) {
          setSelectedVideo(prev => ({ ...prev, completed: false }));
        }
      } else {
        console.error('❌ Failed to unmark video:', result?.error);
        toast({
          title: "Սխալ",
          description: `Չհաջողվեց նշել վիդեոն որպես չդիտած: ${result?.error || 'Անհայտ սխալ'}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('💥 Exception in handleVideoUnmark:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց նշել վիդեոն որպես չդիտած: Անհայտ սխալ",
        variant: "destructive",
      });
    } finally {
      setMarkingComplete(null);
    }
  };

  const handleResetAllProgress = async () => {
    try {
      const result = await resetAllProgress();
      
      if (result?.success) {
        toast({
          title: "Հաջող!",
          description: "Բոլոր վիդեոների պրոգրեսսը զրոյացված է:",
        });
      } else {
        console.error('❌ Failed to reset progress:', result?.error);
        toast({
          title: "Սխալ",
          description: `Չհաջողվեց զրոյացնել պրոգրեսսը: ${result?.error || 'Անհայտ սխալ'}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('💥 Exception in handleResetAllProgress:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց զրոյացնել պրոգրեսսը: Անհայտ սխալ",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground font-bold text-sm sm:text-lg">V</span>
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-sm sm:text-xl font-heading text-foreground truncate">«Վայբ Քոդինգ» Կուրս</h1>
                <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Կատարելագործի սքիլերդ</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-sm text-foreground">Բարի վերադարձ!</p>
              <p className="text-xs text-muted-foreground">{completedCount}/{videos.length} ավարտված</p>
            </div>
            <div className="text-center sm:hidden">
              <p className="text-xs text-muted-foreground">{completedCount}/{videos.length} ավարտված</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleResetAllProgress}
              className="border-border text-muted-foreground hover:bg-muted text-xs sm:text-sm px-2 sm:px-3 mr-2"
            >
              Զրոյացնել պրոգրեսսը
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onLogout}
              className="border-border text-foreground hover:bg-muted text-xs sm:text-sm px-2 sm:px-3"
            >
              <LogOut className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Հելանք</span>
            </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {/* Progress and Notes Section */}
        <div className="mb-6 grid lg:grid-cols-2 gap-6">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading text-foreground">Դասընթացի առաջընթաց</h2>
                <Badge variant="secondary" className="bg-primary/10 text-primary">
                  {Math.round(progressPercentage)}% ավարտված
                </Badge>
              </div>
              <Progress value={progressPercentage} className="h-3" />
              <p className="text-sm text-muted-foreground mt-2">
                {completedCount} / {videos.length} դաս ավարտված
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <h2 className="text-lg font-heading text-foreground mb-4">Էս դասի ռեսուրսները</h2>
              <div className="space-y-3">
                {currentSelectedVideo.resourceLinks && currentSelectedVideo.resourceLinks.length > 0 ? (
                  <div className="space-y-2">
                    {currentSelectedVideo.resourceLinks.map((link, index) => (
                      <a 
                        key={index}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg hover:bg-muted/80 transition-colors text-primary hover:text-primary/80"
                      >
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <span className="text-sm">Դասի նյութեր և լրացուցիչ ռեսուրսներ</span>
                      </a>
                     ))}
                  </div>
                ) : (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      Էս դասի համար լրացուցիչ ռեսուրսներ չկան:
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Video Player */}
          <div className="lg:col-span-2">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center justify-between">
                  {currentSelectedVideo.title}
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 mr-1" />
                    {currentSelectedVideo.duration}
                  </div>
                </CardTitle>
                <p className="text-muted-foreground">{currentSelectedVideo.description}</p>
              </CardHeader>
              
              <CardContent>
                {/* Video Player */}
                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden mb-4">
                  <R2Player
                    key={currentSelectedVideo.id}
                    guid={currentSelectedVideo.guid}
                    videoId={currentSelectedVideo.id}
                    onProgress={handleVideoProgress}
                  />
                  
                  <div className="absolute top-4 right-4">
                    {currentSelectedVideo.completed && (
                      <Badge className="bg-green-500 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Ավարտված
                      </Badge>
                    )}
                  </div>
                  {userLabel && (
                    <div className="absolute bottom-2 left-2 bg-card/60 text-foreground/70 text-[10px] sm:text-xs px-2 py-1 rounded pointer-events-none select-none">
                      {userLabel}
                    </div>
                  )}
                </div>

                {/* Harmar promo banner — animated APNG, opens harmar.ai in a new
                    tab. UTM params let Umami attribute this traffic to the Vibe
                    course banner (harmar.ai reads utm_source/medium/campaign). */}
                <a
                  href="https://harmar.ai/?utm_source=aram-vibe&utm_medium=banner&utm_campaign=vibe-course"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mb-4 rounded-lg overflow-hidden border border-border hover:opacity-90 transition-opacity"
                  aria-label="Harmar — բացել harmar.ai նոր ներդիրում"
                >
                  <img
                    src="/harmar-banner.png"
                    alt="Harmar"
                    loading="lazy"
                    className="w-full h-auto block"
                  />
                </a>

                {/* Resume button if there's a saved position */}
                {videoPositions[currentSelectedVideo.id] && !currentSelectedVideo.completed && (
                  <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <PlayCircle className="w-4 h-4 text-primary" />
                        <span className="text-sm text-foreground">
                          Շարունակել {formatTime(videoPositions[currentSelectedVideo.id])}-ից
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleResumeVideo(currentSelectedVideo.id)}
                        className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        Շարունակել
                      </Button>
                    </div>
                  </div>
                )}

                {currentSelectedVideo.completed ? (
                  <Button 
                    onClick={() => handleVideoUnmark(currentSelectedVideo.id)}
                    disabled={markingComplete === currentSelectedVideo.id}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
                  >
                    {markingComplete === currentSelectedVideo.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Նշվում է...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Չդիտած նշել
                      </>
                    )}
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleVideoComplete(currentSelectedVideo.id)}
                    disabled={markingComplete === currentSelectedVideo.id}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
                  >
                    {markingComplete === currentSelectedVideo.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Նշվում է...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Նշել որպես ավարտված
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Video List */}
          <div className="h-full">
            <Card className="bg-card border-border h-full flex flex-col">
              <CardHeader className="flex-shrink-0">
                <CardTitle className="text-foreground">Դասընթացի բովանդակություն</CardTitle>
              </CardHeader>
              
              <CardContent className="p-0 flex-1 min-h-0 overflow-hidden">
                <div className="h-full overflow-y-auto space-y-1 custom-scrollbar">
                  {videos.map((video, index) => {
                    const savedPosition = videoPositions[video.id];
                    const hasProgress = savedPosition && savedPosition > 30; // Only show if more than 30 seconds watched
                    
                    return (
                      <button
                        key={video.id}
                        onClick={() => setSelectedVideo(video)}
                        className={`w-full p-4 text-left hover:bg-muted/50 transition-colors border-l-4 ${
                          selectedVideo.id === video.id 
                            ? 'border-primary bg-muted/50' 
                            : 'border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                              video.completed 
                                ? 'bg-green-500 text-white' 
                                : hasProgress
                                ? 'bg-primary/20 text-primary border-2 border-primary/40'
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {video.completed ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : hasProgress ? (
                                <PlayCircle className="w-4 h-4" />
                              ) : (
                                index + 1
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-foreground text-sm">{video.title}</p>
                              <div className="flex items-center text-xs text-muted-foreground">
                                <Clock className="w-3 h-3 mr-1" />
                                {video.duration}
                                {hasProgress && (
                                  <span className="ml-2 text-primary">
                                    • Շարունակել {formatTime(savedPosition)}-ից
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            {video.completed ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVideoUnmark(video.id);
                                }}
                                disabled={markingComplete === video.id}
                                className="p-1 text-primary hover:text-primary/80 hover:bg-primary/10 rounded transition-colors"
                                title="Չդիտած նշել"
                              >
                                {markingComplete === video.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4" />
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVideoComplete(video.id);
                                }}
                                disabled={markingComplete === video.id}
                                className="p-1 text-primary hover:text-primary/80 hover:bg-primary/10 rounded transition-colors"
                                title="Ավարտված նշել"
                              >
                                {markingComplete === video.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Course Completion Congratulations Dialog */}
      <AlertDialog open={showCongratulations} onOpenChange={setShowCongratulations}>
        <AlertDialogContent className="max-w-md mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center text-xl font-heading">
              🎉 Շնորհավորում եմ! 🎉
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-base leading-relaxed">
              Դու անցար բոլոր կուրսերը։ Հիմա ստուգի մեյլբոքսդ, հետաքրքիր բան ա սպասում քեզ ընդէ
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-center mt-6">
            <Button onClick={() => setShowCongratulations(false)}>
              Պարզ է
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
